import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@stockpilot/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only SERIALIZES the model the screen already renders; the
 * format lives in `buildDiagnosticsReport` (shared, pure and tested function).
 * It receives the SAME sections array that is rendered, so adding a row above
 * makes it appear in the report without changing anything here. There used to
 * be two sources —the screen on one side and a separate object on the other— and
 * the second would silently fall behind.
 *
 * Labels: `Copy` and `Copied!` are used, which are already translated into the
 * eleven languages. A new string like "Copy diagnostics" would appear in ENGLISH
 * in all of them without the i18n gate complaining, because it only validates
 * what is already in the catalog.
 */
/** How long "Copied!" remains before returning to the normal label. */
const CONFIRMATION_TIMEOUT_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model that the screen renders. */
  sections: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copy = async () => {
    const text = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.sections(),
    });
    try {
      await navigator.clipboard.writeText(text);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_TIMEOUT_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Silencing it
      // would leave the user believing the copy succeeded: mark the failure and leave
      // the report selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copied() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B when clipboard is unavailable: visible report
          to select by hand. Without this, the button would be a dead
          end precisely in environments where debugging is needed most. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.sections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
